import { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";

export default function Zones() {
  const [zones, setZones] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/zones").then(r => { setZones(r.data); setSelected(r.data[0]); }); }, []);

  const maxAvg = Math.max(...zones.map(z => z.avg), 1);
  const minAvg = Math.min(...zones.map(z => z.avg), 1);

  const heatColor = (price) => {
    const t = (price - minAvg) / (maxAvg - minAvg);
    // from deep moss #1F4A3C (31,74,60) to bronze #B08D57 (176,141,87)
    const r = Math.round(31 + (176 - 31) * t);
    const g = Math.round(74 + (141 - 74) * t);
    const b = Math.round(60 + (87 - 60) * t);
    return `rgb(${r},${g},${b})`;
  };

  return (
    <main data-testid="zones-page" className="bg-[#F6F4EE] min-h-screen text-[#0D1F2D]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">§ Index prețuri · Februarie 2026</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight mt-3">Prețuri medii <span className="italic font-medium text-[#0F6E5B]">pe orașe</span>.</h1>

        <div className="mt-12 grid lg:grid-cols-12 gap-6">
          {/* Heatmap grid */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {zones.map((z) => (
              <button
                key={z.key}
                data-testid={`zone-${z.key}`}
                onClick={() => setSelected(z)}
                className={`relative overflow-hidden text-left p-5 min-h-[150px] text-white transition-transform hover:-translate-y-1 ${selected?.key === z.key ? "ring-2 ring-[#0D1F2D] ring-offset-2 ring-offset-[#F6F4EE]" : ""}`}
                style={{ backgroundColor: heatColor(z.avg) }}
              >
                <div className="mono text-[10px] uppercase tracking-[0.22em] opacity-80">{z.key.replace("-", " ")}</div>
                <div className="font-display text-2xl font-bold mt-2 leading-tight">{z.city}</div>
                <div className="mt-6">
                  <div className="font-display text-3xl font-bold">{z.avg}</div>
                  <div className="mono text-[10px] opacity-80">€ / m² (media)</div>
                </div>
                <div className="absolute bottom-3 right-3 mono text-xs flex items-center gap-1">
                  {z.yoy_change_pct > 0 ? <TrendingUp size={12} /> : z.yoy_change_pct < 0 ? <TrendingDown size={12} /> : <Minus size={12} />}
                  <span>{z.yoy_change_pct > 0 ? "+" : ""}{z.yoy_change_pct}%</span>
                </div>
              </button>
            ))}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-5 sticky top-24 self-start">
            {selected && (
              <div className="bg-white border border-[#DEDCCF] p-8">
                <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">{selected.key}</div>
                <h2 className="font-display text-4xl font-bold tracking-tight mt-2">{selected.city}</h2>
                <div className="grid grid-cols-3 gap-3 mt-6 border-y border-[#DEDCCF] py-5">
                  <div><div className="mono text-[10px] uppercase tracking-[0.18em] text-[#52616B]">Min</div><div className="font-display text-xl font-bold">{selected.min}€</div></div>
                  <div><div className="mono text-[10px] uppercase tracking-[0.18em] text-[#0F6E5B]">Media</div><div className="font-display text-xl font-bold text-[#0F6E5B]">{selected.avg}€</div></div>
                  <div><div className="mono text-[10px] uppercase tracking-[0.18em] text-[#52616B]">Max</div><div className="font-display text-xl font-bold">{selected.max}€</div></div>
                </div>
                <div className="mt-6">
                  <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Evoluție 6 luni</div>
                  <div className="h-44 mt-3">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={selected.trend}>
                        <CartesianGrid stroke="#DEDCCF" strokeDasharray="2 4" vertical={false} />
                        <XAxis dataKey="month" stroke="#52616B" style={{ fontSize: 11 }} />
                        <YAxis stroke="#52616B" style={{ fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: "#0D1F2D", border: "none", color: "#F6F4EE", fontSize: 12 }} />
                        <Line dataKey="price" stroke="#0F6E5B" strokeWidth={2} dot={{ r: 3, fill: "#0F6E5B" }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="mt-6 bg-[#F6F4EE] p-4 text-sm text-[#52616B]">
                  Variație anuală: <span className={`mono font-bold ${selected.yoy_change_pct >= 0 ? "text-[#1F4A3C]" : "text-[#0F6E5B]"}`}>{selected.yoy_change_pct > 0 ? "+" : ""}{selected.yoy_change_pct}%</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
